import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  LinearProgress,
  ListItemText,
  MenuItem,
  Select,
  Typography,
  Alert,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from "recharts";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ClientAutocomplete from "../../components/clientAutocomplete";
import AnalyticsWarehousePicker from "../../components/analytics-warehouse-picker";
import MetricKpiCard from "../../components/metrics/MetricKpiCard";
import PeriodTrendChart from "../../components/metrics/PeriodTrendChart";
import FiscalPeriodHistoryTable from "../../components/metrics/FiscalPeriodHistoryTable";
import FiscalYearHistoryTable from "../../components/metrics/FiscalYearHistoryTable";
import FiscalWeekHistoryTable from "../../components/metrics/FiscalWeekHistoryTable";
import DailyDetailTable from "../../components/metrics/DailyDetailTable";
import DataAvailabilitySection from "../../components/metrics/DataAvailabilitySection";
import CalculationFootnote from "../../components/metrics/CalculationFootnote";
import SalesPerformancePageLoading from "../../components/metrics/SalesPerformancePageLoading";
import AnalysisPageTopbar from "../../components/metrics/AnalysisPageTopbar";
import KpiGrid from "../../components/metrics/KpiGrid";
import {
  SECTION_DAILY,
  SECTION_HISTORY,
  SECTION_KPI,
  SECTION_NOTES,
  SECTION_TREND,
  normalizeDashboard,
  visibleKpiIds,
} from "../../components/metrics/salesDashboardConfig";
import {
  useMetricsUi,
  PillTabs,
  LegendItem,
  SectionHead,
  PAGE_FONT,
  MONO_FONT,
} from "../../components/metrics/metricsUi";
import { formatCompactCurrency } from "../../components/metrics/metricsFormat";
import { metricsService } from "../../utils/services/metricsService";
import { buildDailySkuCacheKey } from "../../utils/services/dailySkuCache";
import { orderService } from "../../utils/services/orderService";
import { analyticsWarehouseService } from "../../utils/services/analyticsWarehouseService";
import {
  normalizeWarehouseSelection,
  selectedWarehouseIds,
  warehouseIdsKey,
  warehouseOptionLabel,
  warehouseSelectionSummary,
} from "../../utils/analyticsWarehouses";
import { isMobile } from "../../globalStyles/mobile";

const TAB_YEARLY = "yearly";
const TAB_PERIOD_WEEK = "period_week";
const TAB_DAILY = "daily";
const TAB_PROMOTIONS = "promotions";

const periodStatusSuffix = (period) =>
  period?.timeline_status === "in_progress"
    ? " · in progress"
    : period?.timeline_status === "upcoming"
      ? " · upcoming"
      : "";

// One height for every header control so the filter row lines up exactly.
const CONTROL_HEIGHT = 38;

const visuallyHiddenSx = {
  position: "absolute",
  width: 1,
  height: 1,
  p: 0,
  m: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

// Declared at module scope: defining it inside the page would remount the
// wrapped pickers (and drop their open/search state) on every render.
const FilterField = ({ label, colors, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, minWidth: 0 }}>
    <Typography
      component="span"
      sx={{ fontSize: 12, color: colors.textTertiary, whiteSpace: "nowrap" }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

const MetricsPage = () => {
  const ui = useMetricsUi();
  const { colors } = ui;
  const storedClientId = localStorage.getItem("client_id");
  const [selectedClientData, setSelectedClientData] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [years, setYears] = useState([]);
  const [fiscalYear, setFiscalYear] = useState("");
  const [periodId, setPeriodId] = useState("");
  const [calendarMode, setCalendarMode] = useState("fiscal");
  const [supportsWeeks, setSupportsWeeks] = useState(true);
  const [activeTab, setActiveTab] = useState(TAB_PERIOD_WEEK);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [loading, setLoading] = useState(false);
  const [periodsLoading, setPeriodsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  // Server-resolved dashboard config from the calendar call; the metrics
  // payload carries the same block and wins once loaded.
  const [listedDashboard, setListedDashboard] = useState(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  // Coalesced Order Source values (`source_channel || oms_provider`), same as
  // Order Management. Empty = all sources. Control is hidden when the client
  // has fewer than two distinct sources.
  const [sourceChannelOptions, setSourceChannelOptions] = useState([]);
  const [selectedSourceChannels, setSelectedSourceChannels] = useState([]);
  // Optional warehouse scope for sales + current inventory. [] = All
  // warehouses (no filter — every warehouse of the client). Several selected
  // warehouses are one `$in` scope server-side. The control is hidden, like
  // Sales Channel, unless the client's analytics picker offers at least two
  // warehouses (`warehouseCount` stays null until that probe resolves).
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [warehouseCount, setWarehouseCount] = useState(null);
  const [selectedSalesDay, setSelectedSalesDay] = useState(null);
  const [skuPayload, setSkuPayload] = useState(null);
  const [skuLoading, setSkuLoading] = useState(false);
  const [skuError, setSkuError] = useState(null);
  const skuCacheRef = useRef(new Map());
  const skuRequestRef = useRef(0);
  // `${clientId}|${year}` when the calendar effect itself adopted the server's
  // default year from a response that already carried that year's periods.
  // Lets the re-run triggered by that setFiscalYear skip an identical request.
  const adoptedYearRef = useRef(null);

  const clientId = selectedClientData?._id || storedClientId;
  // Sorted + deduplicated, so request params and cache keys are canonical.
  const warehouseIds = useMemo(
    () => selectedWarehouseIds(selectedWarehouses),
    [selectedWarehouses],
  );
  const warehouseKey = warehouseIdsKey(warehouseIds);
  const hasWarehouseScope = warehouseIds.length > 0;
  const warehouseLabel = warehouseSelectionSummary(selectedWarehouses);
  const warehouseLabelDetail = selectedWarehouses
    .map(warehouseOptionLabel)
    .join(", ");
  const pageLoading = Boolean(clientId) && (periodsLoading || loading);
  // Filters stay usable while data loads; the newest selection wins (each
  // fetch effect cancels the previous request). The skeleton is shown only
  // when there is nothing yet to show — later reloads keep the current data
  // on screen, dimmed, so the page never blanks out on a filter change.
  const hasPayload = Boolean(payload?.metrics);
  const initialLoading = pageLoading && !hasPayload;
  const refreshing = pageLoading && hasPayload;
  const showSourceFilter = sourceChannelOptions.length >= 2;
  // Never hide a filter that is currently applied.
  const showWarehouseFilter =
    hasWarehouseScope || (warehouseCount ?? 0) >= 2;
  const isMonthly = calendarMode === "monthly";
  const periodGrainLabel = isMonthly ? "Month" : "Fiscal Period";
  const yearGrainLabel = isMonthly ? "Year" : "Fiscal Year";
  const dashboard = useMemo(
    () => normalizeDashboard(payload?.dashboard || listedDashboard),
    [payload, listedDashboard],
  );
  const showSection = (id) => dashboard.sections.includes(id);
  const kpiIds = payload?.metrics
    ? visibleKpiIds(dashboard, payload.metrics)
    : [];
  // Detail tabs follow the enabled sections; the bar only shows with 2+ tabs.
  const detailTabs = [
    ...(showSection(SECTION_HISTORY) ? [TAB_YEARLY, TAB_PERIOD_WEEK] : []),
    ...(showSection(SECTION_DAILY) ? [TAB_DAILY] : []),
  ];
  const currentTab = detailTabs.includes(activeTab)
    ? activeTab
    : detailTabs.includes(TAB_PERIOD_WEEK)
      ? TAB_PERIOD_WEEK
      : detailTabs[0];

  // Warehouses belong to a client; never carry one — or the previous
  // client's numbers — across a client switch.
  useEffect(() => {
    setSelectedWarehouses([]);
    setWarehouseCount(null);
    setPayload(null);
    setListedDashboard(null);
    if (!clientId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await analyticsWarehouseService.fetch({
          clientId,
          limit: 2,
        });
        if (cancelled) return;
        const rows = res?.data || (Array.isArray(res) ? res : []);
        setWarehouseCount(Number.isFinite(res?.total) ? res.total : rows.length);
      } catch {
        if (!cancelled) setWarehouseCount(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  useEffect(() => {
    if (!clientId) {
      setSourceChannelOptions([]);
      setSelectedSourceChannels([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await orderService.fetchOrderSource("", clientId, 50, 0);
        if (cancelled) return;
        const options = (data?.data || [])
          .map((row) => row?._id)
          .filter((value) => value != null && value !== "");
        setSourceChannelOptions(options);
        setSelectedSourceChannels((prev) => {
          const next = prev.filter((value) => options.includes(value));
          if (
            next.length === prev.length &&
            next.every((value, index) => value === prev[index])
          ) {
            return prev;
          }
          return next;
        });
      } catch {
        if (!cancelled) {
          setSourceChannelOptions([]);
          setSelectedSourceChannels([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  useEffect(() => {
    if (!clientId) {
      setPeriods([]);
      setYears([]);
      setFiscalYear("");
      setPeriodId("");
      setCalendarMode("fiscal");
      setSupportsWeeks(true);
      return;
    }
    const adoptedKey = adoptedYearRef.current;
    adoptedYearRef.current = null;
    if (fiscalYear && adoptedKey === `${clientId}|${fiscalYear}`) return;
    let cancelled = false;
    (async () => {
      try {
        setPeriodsLoading(true);
        const data = await metricsService.fetchFiscalPeriods({
          clientId,
          fiscalYear: fiscalYear || undefined,
        });
        if (cancelled) return;
        setListedDashboard(data?.dashboard || null);
        const mode = data?.calendar_mode === "monthly" ? "monthly" : "fiscal";
        setCalendarMode(mode);
        setSupportsWeeks(
          data?.supports_weeks != null ? !!data.supports_weeks : mode !== "monthly",
        );
        const yearList = data?.years || [];
        setYears(yearList);
        const nextFy =
          data?.default_fiscal_year ||
          yearList[yearList.length - 1]?.fiscal_year ||
          "";
        if (!fiscalYear || !yearList.some((y) => y.fiscal_year === fiscalYear)) {
          // The periods below are the server's default year's, so re-fetching
          // them for `nextFy` would return the same payload.
          if (
            nextFy &&
            nextFy !== fiscalYear &&
            nextFy === data?.default_fiscal_year
          ) {
            adoptedYearRef.current = `${clientId}|${nextFy}`;
          }
          setFiscalYear(nextFy);
        }
        const list = data?.periods || [];
        setPeriods(list);
        const nextPeriod =
          data?.default_period_id ||
          list.find((p) => p.timeline_status === "in_progress")?.id ||
          list[list.length - 1]?.id ||
          "";
        // Keep current period if it still belongs to the loaded year.
        setPeriodId((prev) =>
          list.some((p) => p.id === prev) ? prev : nextPeriod,
        );
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to load analysis periods",
          );
        }
      } finally {
        if (!cancelled) setPeriodsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId, fiscalYear]);

  useEffect(() => {
    skuCacheRef.current = new Map();
    skuRequestRef.current += 1;
    setSelectedSalesDay(null);
    setSkuPayload(null);
    setSkuError(null);
    setSkuLoading(false);
    if (!periodId || !clientId) {
      setPayload(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await metricsService.fetchSalesPerformance({
          clientId,
          periodId,
          fiscalYear: fiscalYear || undefined,
          includeDaily: true,
          includeHistory: true,
          sourceChannels: selectedSourceChannels,
          warehouseIds: warehouseKey ? warehouseKey.split(",") : [],
        });
        if (!cancelled) {
          setPayload(data);
          if (data?.calendar_mode === "monthly" || data?.calendar_mode === "fiscal") {
            setCalendarMode(data.calendar_mode);
          }
          if (data?.supports_weeks != null) {
            setSupportsWeeks(!!data.supports_weeks);
          }
          setSelectedWeek(null);
        }
      } catch (err) {
        if (!cancelled) {
          setPayload(null);
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to load metrics",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    periodId,
    clientId,
    fiscalYear,
    reloadNonce,
    selectedSourceChannels,
    warehouseKey,
  ]);

  const loadDailySkuDay = async (date) => {
    const key = buildDailySkuCacheKey(
      clientId,
      date,
      selectedSourceChannels,
      warehouseIds,
    );
    const cached = skuCacheRef.current.get(key);
    if (cached) {
      setSkuPayload(cached);
      setSkuError(null);
      setSkuLoading(false);
      return;
    }
    const requestId = skuRequestRef.current + 1;
    skuRequestRef.current = requestId;
    setSkuLoading(true);
    setSkuError(null);
    setSkuPayload(null);
    try {
      const data = await metricsService.fetchDailySkuBreakdown({
        clientId,
        salesDay: date,
        sourceChannels: selectedSourceChannels,
        warehouseIds,
      });
      skuCacheRef.current.set(key, data);
      if (skuRequestRef.current !== requestId) return;
      setSkuPayload(data);
    } catch (err) {
      if (skuRequestRef.current !== requestId) return;
      setSkuPayload(null);
      setSkuError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load SKU breakdown",
      );
    } finally {
      if (skuRequestRef.current === requestId) setSkuLoading(false);
    }
  };

  const handleSelectDailySkuDay = (date) => {
    if (selectedSalesDay === date) {
      skuRequestRef.current += 1;
      setSelectedSalesDay(null);
      setSkuPayload(null);
      setSkuError(null);
      setSkuLoading(false);
      return;
    }
    setSelectedSalesDay(date);
    loadDailySkuDay(date);
  };

  const retryDailySkuDay = () => {
    if (!selectedSalesDay) return;
    const key = buildDailySkuCacheKey(
      clientId,
      selectedSalesDay,
      selectedSourceChannels,
      warehouseIds,
    );
    skuCacheRef.current.delete(key);
    loadDailySkuDay(selectedSalesDay);
  };

  const chartData = useMemo(() => {
    if (!payload?.daily) return [];
    const daily = selectedWeek
      ? payload.daily.filter(
          (row) =>
            row.date >= selectedWeek.start_date &&
            row.date <= selectedWeek.end_date,
        )
      : payload.daily;
    return daily.map((row) => ({
      date: row.date.slice(5),
      fullDate: row.date,
      gross_sales: row.gross_sales,
      orders: row.orders,
      units_sold: row.units_sold,
    }));
  }, [payload, selectedWeek]);

  const selectedPeriod = periods.find((p) => p.id === periodId);
  const selectedYearId = fiscalYear || selectedPeriod?.fiscal_year || null;
  const priorLabel = payload?.prior_period?.label || null;
  const hasDailyActivity = chartData.some(
    (row) => row.gross_sales || row.orders || row.units_sold,
  );

  const selectSx = {
    height: CONTROL_HEIGHT,
    backgroundColor: colors.bgPaper,
    fontSize: 13,
    fontWeight: 600,
    borderRadius: "8px",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: ui.borderStrong,
    },
  };

  // Makes the shared autocompletes (Client, Warehouse) read as the same
  // control family as the Selects: same height, radius, border and type.
  const autocompleteInputSx = {
    margin: 0,
    mt: 0,
    mb: 0,
    "&.MuiFormControl-marginDense": { margin: 0 },
    "& .MuiInputLabel-root": { display: "none" },
    "& .MuiOutlinedInput-root": {
      height: CONTROL_HEIGHT,
      py: 0,
      fontSize: 13,
      fontWeight: 600,
      fontFamily: PAGE_FONT,
      borderRadius: "8px",
      backgroundColor: colors.bgPaper,
      color: colors.textPrimary,
      "& fieldset": { borderColor: ui.borderStrong },
      "&:hover fieldset": { borderColor: colors.textTertiary },
    },
    "& .MuiInputBase-input::placeholder": {
      color: colors.textPrimary,
      fontWeight: 600,
      opacity: 1,
    },
  };

  const tooltipStyle = {
    backgroundColor: colors.bgPaper,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    color: colors.textPrimary,
    fontSize: 12,
  };

  const handleSelectYear = (yearRow) => {
    const nextFy = yearRow.fiscal_year || yearRow.year_id;
    // Re-selecting the active year would be a no-op `setFiscalYear`, so the
    // period reload below never runs and `periodId` would stay blank forever.
    if (!nextFy || nextFy === selectedYearId) return;
    setFiscalYear(nextFy);
    setSelectedWeek(null);
    setActiveTab(TAB_PERIOD_WEEK);
    // periodId will be corrected when calendar reload returns default_period_id
    setPeriodId("");
  };

  const handleFiscalYearChange = (nextFy) => {
    if (!nextFy || nextFy === selectedYearId) return;
    setFiscalYear(nextFy);
    setSelectedWeek(null);
    setPeriodId("");
  };

  const handleSelectWeek = (weekRow) => {
    setSelectedWeek(weekRow);
    setActiveTab(TAB_DAILY);
  };

  return (
    <Box
      sx={{
        backgroundColor: ui.darkMode ? colors.bgPrimary : "#F5F6F8",
        minHeight: "100%",
        fontFamily: PAGE_FONT,
      }}
    >
      <Box
        sx={{
          maxWidth: 1480,
          mx: "auto",
          padding: isMobile() ? "14px 12px 40px" : "22px 28px 56px",
        }}
      >
        {/* ---------- Title row ---------- */}
        <AnalysisPageTopbar
          badge="ST"
          title="Sales over Time"
          clientName={
            selectedClientData?.business_name || selectedClientData?.name
          }
          mb={1.75}
          right={
            years.length > 0 || !storedClientId ? (
              <>
                {years.length > 0 && (
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: colors.textSecondary,
                      display: isMobile() ? "none" : "flex",
                      alignItems: "center",
                      gap: 0.8,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        backgroundColor: ui.positive,
                        boxShadow: `0 0 0 3px ${ui.positiveBg}`,
                        display: "inline-block",
                      }}
                    />
                    {years.length} {isMonthly ? "calendar" : "fiscal"} year
                    {years.length === 1 ? "" : "s"} available
                  </Typography>
                )}
                {!storedClientId && (
                  <FilterField label="Client" colors={colors}>
                    <ClientAutocomplete
                      width={220}
                      hideLabel
                      margin="none"
                      style={{ marginRight: 0, width: 220 }}
                      inputSx={autocompleteInputSx}
                      selectedClient={selectedClientData}
                      onClientChange={(client) => setSelectedClientData(client)}
                    />
                  </FilterField>
                )}
              </>
            ) : null
          }
        />

        {/* ---------- Filter bar ----------
            Every control that changes the numbers lives on this one row, so
            the admin-only Client picker (title row) can never push a filter
            onto a line of its own. */}
        <Box
          role="toolbar"
          aria-label="Sales filters"
          sx={{
            position: "relative",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            columnGap: 2,
            rowGap: 1.25,
            pb: 1.75,
            mb: 2.5,
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <FilterField label="Year" colors={colors}>
            <FormControl size="small">
              <Select
                value={fiscalYear}
                disabled={!years.length}
                onChange={(e) => handleFiscalYearChange(e.target.value)}
                displayEmpty
                SelectDisplayProps={{ "aria-label": "Year" }}
                sx={{ ...selectSx, minWidth: 104 }}
              >
                {years.map((year) => (
                  <MenuItem
                    key={year.fiscal_year}
                    value={year.fiscal_year}
                    sx={{ fontSize: 13 }}
                  >
                    {year.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FilterField>

          <FilterField label="Viewing" colors={colors}>
            <FormControl size="small">
              <Select
                value={periodId}
                // Options are being replaced while a new year's calendar loads.
                disabled={periodsLoading || !periods.length}
                onChange={(e) => {
                  setPeriodId(e.target.value);
                  setSelectedWeek(null);
                }}
                displayEmpty
                SelectDisplayProps={{ "aria-label": periodGrainLabel }}
                // The date range is already printed under the filters, so
                // the closed control stays compact; the menu keeps it.
                renderValue={(value) => {
                  const period = periods.find((p) => p.id === value);
                  if (!period) return "";
                  return `${period.label}${periodStatusSuffix(period)}`;
                }}
                sx={{ ...selectSx, minWidth: 200 }}
              >
                {periods.map((period) => (
                  <MenuItem
                    key={period.id}
                    value={period.id}
                    sx={{ fontSize: 13 }}
                  >
                    {period.label} — {period.start_date} – {period.end_date}
                    {periodStatusSuffix(period)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FilterField>

          {showSourceFilter && (
            <FilterField label="Sales Channel" colors={colors}>
              <FormControl size="small">
                <Select
                  multiple
                  displayEmpty
                  value={selectedSourceChannels}
                  onChange={(e) => {
                    const next = e.target.value;
                    setSelectedSourceChannels(
                      typeof next === "string" ? next.split(",") : next,
                    );
                  }}
                  SelectDisplayProps={{ "aria-label": "Sales Channel" }}
                  renderValue={(selected) =>
                    !selected?.length
                      ? "All channels"
                      : selected.length === 1
                        ? selected[0]
                        : `${selected.length} channels`
                  }
                  sx={{ ...selectSx, minWidth: 150 }}
                >
                  {sourceChannelOptions.map((option) => (
                    <MenuItem key={option} value={option} sx={{ fontSize: 13 }}>
                      <Checkbox
                        size="small"
                        checked={selectedSourceChannels.includes(option)}
                      />
                      <ListItemText
                        primary={option}
                        primaryTypographyProps={{ fontSize: 13 }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </FilterField>
          )}

          {showWarehouseFilter && (
            <FilterField label="Warehouse" colors={colors}>
              <Box data-testid="sales-warehouse-filter">
                <AnalyticsWarehousePicker
                  clientId={clientId}
                  mode="multi"
                  value={selectedWarehouses}
                  onChange={(next) =>
                    setSelectedWarehouses(normalizeWarehouseSelection(next))
                  }
                  disabled={!clientId}
                  width={220}
                  height={CONTROL_HEIGHT}
                  triggerSx={{
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: PAGE_FONT,
                    borderRadius: "8px",
                    backgroundColor: colors.bgPaper,
                    color: colors.textPrimary,
                    borderColor: ui.borderStrong,
                    "&:hover": {
                      borderColor: colors.textTertiary,
                      backgroundColor: colors.bgPaper,
                    },
                  }}
                />
              </Box>
            </FilterField>
          )}

          <Button
            variant="outlined"
            size="small"
            disabled={pageLoading || !clientId || !periodId}
            onClick={() => setReloadNonce((n) => n + 1)}
            startIcon={
              pageLoading ? (
                <CircularProgress size={13} thickness={5} color="inherit" />
              ) : (
                <RefreshIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{
              ml: "auto",
              height: CONTROL_HEIGHT,
              minWidth: 112,
              textTransform: "none",
              fontSize: 12.5,
              fontWeight: 600,
              borderRadius: "8px",
              borderColor: ui.borderStrong,
              color: colors.textSecondary,
              backgroundColor: colors.bgPaper,
              "&.Mui-disabled": {
                borderColor: ui.borderStrong,
                color: colors.textTertiary,
              },
            }}
          >
            {pageLoading ? "Updating…" : "Refresh"}
          </Button>

          {/* Thin progress rail on the divider while existing data refreshes. */}
          {refreshing && (
            <LinearProgress
              aria-hidden
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: -1,
                height: 2,
                borderRadius: 1,
                backgroundColor: "transparent",
                "& .MuiLinearProgress-bar": { backgroundColor: ui.accent },
              }}
            />
          )}
          <Box role="status" aria-live="polite" sx={visuallyHiddenSx}>
            {refreshing ? "Updating sales data" : ""}
          </Box>
        </Box>

        {!clientId && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: "10px" }}>
            Select a client to load sales performance metrics.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>
            {error}
          </Alert>
        )}

        {initialLoading && (
          <SalesPerformancePageLoading
            kpiCount={
              showSection(SECTION_KPI) ? dashboard.kpi_metrics.length : 0
            }
            showChart={showSection(SECTION_TREND)}
            showTable={detailTabs.length > 0}
            KpiGridComponent={KpiGrid}
          />
        )}

        {hasPayload && (
          <Box
            aria-busy={refreshing}
            sx={{
              opacity: refreshing ? 0.5 : 1,
              pointerEvents: refreshing ? "none" : "auto",
              // Delay the dim so fast responses never flicker.
              transition: refreshing
                ? "opacity 180ms ease 150ms"
                : "opacity 120ms ease",
            }}
          >
            {/* ---------- KPI context ---------- */}
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                flexWrap: "wrap",
                gap: 1.25,
                mb: 1.25,
              }}
            >
              <Typography
                sx={{
                  fontSize: 17,
                  fontWeight: 700,
                  color: colors.textPrimary,
                  letterSpacing: "-0.01em",
                }}
              >
                {selectedPeriod?.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: colors.textTertiary,
                  fontFamily: MONO_FONT,
                }}
              >
                {selectedPeriod?.start_date} – {selectedPeriod?.end_date}
              </Typography>
              {priorLabel && showSection(SECTION_KPI) && (
                <Typography sx={{ fontSize: 12, color: colors.textTertiary }}>
                  · compared with {priorLabel}
                </Typography>
              )}
              {hasWarehouseScope && (
                <Box
                  data-testid="sales-warehouse-scope"
                  sx={{
                    alignSelf: "center",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.6,
                    ml: 0.5,
                    pl: 1,
                    pr: 0.4,
                    height: 24,
                    borderRadius: "12px",
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: ui.accent,
                    backgroundColor: ui.accentSoft,
                    maxWidth: 360,
                  }}
                >
                  <WarehouseOutlinedIcon sx={{ fontSize: 14 }} />
                  <Box
                    component="span"
                    title={warehouseLabelDetail}
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {selectedWarehouses.length > 1
                      ? `${warehouseLabel}: ${warehouseLabelDetail}`
                      : warehouseLabel}
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    aria-label="Clear warehouse filter"
                    title="Show all warehouses"
                    onClick={() => setSelectedWarehouses([])}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 18,
                      height: 18,
                      p: 0,
                      border: 0,
                      borderRadius: "50%",
                      background: "transparent",
                      color: "inherit",
                      cursor: "pointer",
                      "&:hover": { backgroundColor: ui.accent, color: "#fff" },
                      "&:focus-visible": {
                        outline: `2px solid ${ui.accent}`,
                        outlineOffset: 1,
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 12 }} />
                  </Box>
                </Box>
              )}
            </Box>

            {/* ---------- KPI grid ---------- */}
            {showSection(SECTION_KPI) && kpiIds.length > 0 && (
              <Box component="section" aria-label="Key metrics">
                <KpiGrid>
                  {kpiIds.map((id) => (
                    <MetricKpiCard
                      key={id}
                      metric={payload.metrics[id]}
                      priorLabel={priorLabel}
                    />
                  ))}
                </KpiGrid>
              </Box>
            )}

            {/* ---------- Hero trend ---------- */}
            {showSection(SECTION_TREND) && (
              <PeriodTrendChart
                periodHistory={payload.period_history || []}
                selectedPeriodId={periodId}
                sourceOptions={sourceChannelOptions}
                onSelectPeriod={(id) => {
                  setPeriodId(id);
                  setSelectedWeek(null);
                }}
                title={
                  isMonthly
                    ? `Gross Sales & Orders by Month (${
                        payload.period_history?.[0]?.fiscal_year ||
                        selectedYearId ||
                        "selected year"
                      })`
                    : undefined
                }
                clickHint={
                  isMonthly
                    ? "Click a bar to switch month"
                    : "Click a bar to switch period"
                }
              />
            )}

            {/* ---------- Detail tabs ---------- */}
            {detailTabs.length > 1 && (
              <Box sx={{ mb: 2 }}>
                <PillTabs
                  value={currentTab}
                  onChange={setActiveTab}
                  tabs={[
                    ...(showSection(SECTION_HISTORY)
                      ? [
                          { value: TAB_YEARLY, label: "Yearly" },
                          {
                            value: TAB_PERIOD_WEEK,
                            label: periodGrainLabel,
                          },
                        ]
                      : []),
                    ...(showSection(SECTION_DAILY)
                      ? [
                          {
                            value: TAB_DAILY,
                            label: "Daily Detail",
                            badge: payload.daily?.length || null,
                          },
                        ]
                      : []),
                    ...(showSection(SECTION_HISTORY)
                      ? [
                          {
                            value: TAB_PROMOTIONS,
                            label: "Promotions",
                            disabled: true,
                            tooltip:
                              "Promotions analysis will ship in a later phase",
                          },
                        ]
                      : []),
                  ]}
                />
              </Box>
            )}

            {currentTab === TAB_YEARLY && (
              <FiscalYearHistoryTable
                yearHistory={payload.year_history || []}
                selectedYearId={selectedYearId}
                onSelectYear={handleSelectYear}
                title={
                  isMonthly
                    ? "Yearly performance"
                    : "Fiscal year performance"
                }
                subtitle={
                  isMonthly
                    ? "Aggregated from this client's available calendar years. Partial / in-progress years are labeled — totals are not extrapolated to a full year. Select a year to open its months."
                    : undefined
                }
                yearColumnLabel={yearGrainLabel}
              />
            )}

            {currentTab === TAB_PERIOD_WEEK && (
              <>
                {supportsWeeks && (
                  <FiscalWeekHistoryTable
                    weekHistory={payload.week_history || []}
                    daily={payload.daily || []}
                    selectedWeekId={selectedWeek?.week_id}
                    onSelectWeek={
                      showSection(SECTION_DAILY) ? handleSelectWeek : undefined
                    }
                  />
                )}
                <FiscalPeriodHistoryTable
                  periodHistory={payload.period_history || []}
                  selectedPeriodId={periodId}
                  onSelectPeriod={(id) => {
                    setPeriodId(id);
                    setSelectedWeek(null);
                  }}
                  title={
                    isMonthly
                      ? "Monthly performance"
                      : "Fiscal period performance"
                  }
                  subtitle={
                    isMonthly
                      ? "Complete calendar year (January–December). Inventory is not included — current on-hand is live, not month-end stock. Select a month to load daily detail."
                      : undefined
                  }
                  periodColumnLabel={periodGrainLabel}
                />
              </>
            )}

            {currentTab === TAB_DAILY && (
              <>
                <SectionHead
                  title="Daily trend"
                  subtitle={
                    selectedWeek
                      ? `Filtered to ${selectedWeek.label} · ${selectedWeek.start_date} – ${selectedWeek.end_date}`
                      : isMonthly
                        ? "All days in the selected month"
                        : "All days in the selected fiscal period"
                  }
                  right={
                    <Box sx={{ display: "flex", gap: 1.75 }}>
                      <LegendItem color={ui.accent} label="Gross Sales $" />
                      <LegendItem color="#7C3AED" label="Units Sold" />
                      <LegendItem color={ui.positive} label="Orders" />
                    </Box>
                  }
                />

                {!hasDailyActivity ? (
                  <Box
                    sx={{
                      ...ui.card,
                      height: 130,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: 13, color: colors.textTertiary }}>
                      No daily activity in this{" "}
                      {selectedWeek ? "week" : "period"}
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: isMobile() ? "1fr" : "2fr 1fr",
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ ...ui.card, p: "14px 10px 6px", height: 300 }}>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: colors.textSecondary,
                          px: 1,
                          mb: 0.5,
                        }}
                      >
                        Gross Sales ($) and Units Sold
                      </Typography>
                      <ResponsiveContainer width="100%" height="88%">
                        <AreaChart data={chartData}>
                          <CartesianGrid
                            stroke={colors.gridLine}
                            strokeDasharray="3 3"
                            vertical={false}
                          />
                          <XAxis
                            dataKey="date"
                            tickLine={false}
                            axisLine={{ stroke: colors.border }}
                            tick={{
                              fill: colors.textTertiary,
                              fontSize: 10.5,
                              fontFamily: MONO_FONT,
                            }}
                          />
                          <YAxis
                            yAxisId="sales"
                            tickFormatter={formatCompactCurrency}
                            tickLine={false}
                            axisLine={false}
                            tick={{
                              fill: colors.textTertiary,
                              fontSize: 10.5,
                              fontFamily: MONO_FONT,
                            }}
                            width={44}
                          />
                          <YAxis
                            yAxisId="units"
                            orientation="right"
                            tickLine={false}
                            axisLine={false}
                            tick={{
                              fill: colors.textTertiary,
                              fontSize: 10.5,
                              fontFamily: MONO_FONT,
                            }}
                            width={36}
                          />
                          <RechartsTooltip contentStyle={tooltipStyle} />
                          <Area
                            yAxisId="sales"
                            type="monotone"
                            dataKey="gross_sales"
                            name="Gross Sales"
                            stroke={ui.accent}
                            fill={ui.accent}
                            fillOpacity={0.14}
                            strokeWidth={2}
                          />
                          <Area
                            yAxisId="units"
                            type="monotone"
                            dataKey="units_sold"
                            name="Units Sold"
                            stroke="#7C3AED"
                            fill="#7C3AED"
                            fillOpacity={0.07}
                            strokeWidth={2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Box>
                    <Box sx={{ ...ui.card, p: "14px 10px 6px", height: 300 }}>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: colors.textSecondary,
                          px: 1,
                          mb: 0.5,
                        }}
                      >
                        Orders
                      </Typography>
                      <ResponsiveContainer width="100%" height="88%">
                        <BarChart data={chartData}>
                          <CartesianGrid
                            stroke={colors.gridLine}
                            strokeDasharray="3 3"
                            vertical={false}
                          />
                          <XAxis
                            dataKey="date"
                            tickLine={false}
                            axisLine={{ stroke: colors.border }}
                            tick={{
                              fill: colors.textTertiary,
                              fontSize: 10,
                              fontFamily: MONO_FONT,
                            }}
                            interval="preserveStartEnd"
                          />
                          <YAxis
                            allowDecimals={false}
                            tickLine={false}
                            axisLine={false}
                            tick={{
                              fill: colors.textTertiary,
                              fontSize: 10.5,
                              fontFamily: MONO_FONT,
                            }}
                            width={30}
                          />
                          <RechartsTooltip
                            contentStyle={tooltipStyle}
                            cursor={{ fill: ui.surfaceAlt, opacity: 0.6 }}
                          />
                          <Bar
                            dataKey="orders"
                            name="Orders"
                            fill={ui.positive}
                            radius={[3, 3, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                )}

                <DailyDetailTable
                  daily={payload.daily || []}
                  weekFilter={supportsWeeks ? selectedWeek : null}
                  onClearWeekFilter={() => setSelectedWeek(null)}
                  allDaysSubtitle={
                    isMonthly
                      ? "All days in the selected month"
                      : "All days in the selected fiscal period"
                  }
                  selectedDate={selectedSalesDay}
                  onSelectDay={handleSelectDailySkuDay}
                  skuState={
                    selectedSalesDay
                      ? {
                          loading: skuLoading,
                          error: skuError,
                          payload: skuPayload,
                          onRetry: retryDailySkuDay,
                        }
                      : null
                  }
                />
              </>
            )}

            {showSection(SECTION_NOTES) && (
              <>
                <DataAvailabilitySection metrics={payload.metrics} />

                <CalculationFootnote
                  salesDefinition={payload.sales_definition}
                  audit={payload.audit}
                />
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MetricsPage;
